import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DsButton from '../../DsButton';
import ChatImageModal from '../composer/ChatImageModal';
import { SkeletonAvatar, SkeletonButton, SkeletonLine, SkeletonMedia } from '../../Skeleton';
import { formatDateTimeJST } from '../../../../utils/datetime';
import { appendVariant, buildChatAssetUrl } from '../composer/chatAssetUtils';
import { useConfirm } from '../../../../design-system/ConfirmDialog';
import { useToast } from '../../ToastSystem';
import { downloadBlobFromUrl } from '../../../../api/storage';
import {
  createChatNoteByUrl,
  deleteChatNoteByUrl,
  getChatNoteDetailByUrl,
  listChatNotesByUrl,
  updateChatNoteByUrl,
  uploadNoteImageThroughApi,
} from '../../../../api/chatNotes';
import { linkifyTextNodes } from '../timeline/linkifyTextNodes';

const API_URL = process.env.REACT_APP_API_URL;
const ADMIN_PROFILE_IMAGE_PATH = process.env.REACT_APP_ADMIN_PROFILE_IMAGE_PATH;
const ARTIST_PROFILE_IMAGE_PATH = process.env.REACT_APP_ARTIST_PROFILE_IMAGE_PATH;
const MAX_IMAGES = 20;
const NOTE_IMAGE_MAX_BYTES = 30 * 1024 * 1024;
const NOTE_IMAGE_MAX_MB = 30;

const getNoteErrorMessage = (error, fallback) => {
  const message = typeof error?.message === 'string' ? error.message.trim() : '';
  return message || fallback;
};

const renderContentWithLinks = (text) => {
  if (!text) return null;
  return linkifyTextNodes(text).map((node) =>
    React.isValidElement(node) && node.type === 'a'
      ? React.cloneElement(node, { onClick: (event) => event.stopPropagation() })
      : node
  );
};

const buildProfileImageUrl = (note) => {
  const file = String(note?.author_profile_picture || '').trim();
  if (!file) return '';
  const root = note?.author_type === 'admin' ? ADMIN_PROFILE_IMAGE_PATH : ARTIST_PROFILE_IMAGE_PATH;
  return appendVariant(buildChatAssetUrl({ apiUrl: API_URL, root, file }), 'proxy');
};

const getAuthorInitial = (note) => {
  const text = String(note?.author_display_name || note?.artist_name || note?.admin_full_name || '不').trim();
  return (text.charAt(0) || '不').toUpperCase();
};

const formatNoteImageExpiryLabel = (expiresAt) => {
  const formatted = formatDateTimeJST(expiresAt);
  if (!formatted) return '';
  const currentYear = new Date().getFullYear();
  const yearPrefix = `${currentYear}/`;
  const displayValue = formatted.startsWith(yearPrefix) ? formatted.slice(yearPrefix.length) : formatted;
  return `画像期限: ${displayValue}`;
};

const getNoteImageDownloadName = (image, index) => {
  const preferred = String(image?.original_name || image?.file_name || '').trim();
  if (preferred) return preferred;
  return `note-image-${index + 1}.jpg`;
};

const pickNoteImagePreviewSrc = (image) => image?.proxy_url || image?.proxyUrl || image?.url || '';
const pickNoteImageFullSrc = (image) => image?.url || image?.downloadUrl || image?.proxy_url || image?.proxyUrl || '';
const pickNoteImageFallbackSrc = (image) => {
  const previewSrc = pickNoteImagePreviewSrc(image);
  const fullSrc = pickNoteImageFullSrc(image);
  return fullSrc && fullSrc !== previewSrc ? fullSrc : '';
};

const NoteImage = ({ image, className = '', alt = '', loading }) => {
  const previewSrc = pickNoteImagePreviewSrc(image);
  const fallbackSrc = pickNoteImageFallbackSrc(image);
  const [src, setSrc] = useState(previewSrc);

  useEffect(() => {
    setSrc(previewSrc);
  }, [previewSrc]);

  if (!src) return null;

  return (
    <img
      className={className}
      src={src}
      alt={alt}
      loading={loading}
      onError={() => {
        if (fallbackSrc && src !== fallbackSrc) {
          setSrc(fallbackSrc);
        }
      }}
    />
  );
};

const ChatNotesPanel = ({
  ownerId,
  rootClassName,
  title = 'ノート',
  buildListUrl,
  createUrl,
  buildUpdateUrl,
  buildDeleteUrl,
  buildDetailUrl,
  onModeChange,
  openDetailEventName = '',
  closeDetailEventName = '',
  closeImageEventName = '',
  emptyText = 'ノートはまだありません。',
  onNotesChanged,
  canCreateNote = true,
  canManageNotes = true,
}) => {
  const [notes, setNotes] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const confirmDialog = useConfirm();
  const { error: toastError } = useToast();
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState('list');
  const [detail, setDetail] = useState(null);
  const [titleValue, setTitleValue] = useState('');
  const [bodyValue, setBodyValue] = useState('');
  const [existingImages, setExistingImages] = useState([]);
  const [removedOrders, setRemovedOrders] = useState(new Set());
  const [newImages, setNewImages] = useState([]);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [imageModalIndex, setImageModalIndex] = useState(0);
  const fileRef = useRef(null);
  const offsetRef = useRef(0);
  const loadingRef = useRef(false);
  const onModeChangeRef = useRef(onModeChange);
  const onNotesChangedRef = useRef(onNotesChanged);
  const buildListUrlRef = useRef(buildListUrl);
  const buildDetailUrlRef = useRef(buildDetailUrl);
  const buildUpdateUrlRef = useRef(buildUpdateUrl);
  const buildDeleteUrlRef = useRef(buildDeleteUrl);

  const limit = 20;

  useEffect(() => {
    onModeChangeRef.current = onModeChange;
  }, [onModeChange]);

  useEffect(() => {
    onNotesChangedRef.current = onNotesChanged;
  }, [onNotesChanged]);

  useEffect(() => {
    buildListUrlRef.current = buildListUrl;
  }, [buildListUrl]);

  useEffect(() => {
    buildDetailUrlRef.current = buildDetailUrl;
  }, [buildDetailUrl]);

  useEffect(() => {
    buildUpdateUrlRef.current = buildUpdateUrl;
  }, [buildUpdateUrl]);

  useEffect(() => {
    buildDeleteUrlRef.current = buildDeleteUrl;
  }, [buildDeleteUrl]);

  const emitMode = useCallback((nextMode) => {
    setMode(nextMode);
    if (typeof onModeChangeRef.current === 'function') {
      onModeChangeRef.current(nextMode);
    }
  }, []);

  const resetCompose = useCallback(() => {
    setTitleValue('');
    setBodyValue('');
    setExistingImages([]);
    setRemovedOrders(new Set());
    setNewImages([]);
    if (fileRef.current) fileRef.current.value = '';
  }, []);

  const fetchNotes = useCallback(async (reset = false) => {
    if (!ownerId || loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    try {
      const nextOffset = reset ? 0 : offsetRef.current;
      const json = await listChatNotesByUrl(buildListUrlRef.current(ownerId, nextOffset, limit));
      const rows = Array.isArray(json?.notes) ? json.notes : [];
      const draftRows = Array.isArray(json?.drafts) ? json.drafts : [];
      setNotes((prev) => (reset ? rows : [...prev, ...rows]));
      if (reset) setDrafts(draftRows);
      const resolvedOffset = nextOffset + rows.length;
      offsetRef.current = resolvedOffset;
      setHasMore(rows.length === limit);
    } catch (error) {
      console.error('notes fetch error:', error);
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [limit, ownerId]);

  const openDetail = useCallback(async (noteId, options = {}) => {
    if (!noteId) return;
    try {
      const baseUrl = buildDetailUrlRef.current(noteId);
      const detailUrl = options.version === 'draft'
        ? `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}version=draft`
        : baseUrl;
      const json = await getChatNoteDetailByUrl(detailUrl);
      setDetail(json);
      emitMode({ type: 'detail', noteId });
    } catch (error) {
      console.error('note detail error:', error);
      toastError('ノート情報の取得に失敗しました。');
    }
  }, [toastError, emitMode]);

  useEffect(() => {
    setNotes([]);
    setDrafts([]);
    offsetRef.current = 0;
    setHasMore(true);
    setDetail(null);
    resetCompose();
    emitMode('list');
    if (ownerId) {
      fetchNotes(true);
    }
  }, [ownerId, emitMode, fetchNotes, resetCompose]);

  useEffect(() => {
    if (!openDetailEventName) return undefined;
    const handleOpen = (event) => {
      const noteId = event?.detail?.noteId;
      if (noteId) openDetail(noteId);
    };
    window.addEventListener(openDetailEventName, handleOpen);
    return () => window.removeEventListener(openDetailEventName, handleOpen);
  }, [openDetailEventName, openDetail]);

  useEffect(() => {
    if (!closeDetailEventName) return undefined;
    const handleClose = () => {
      setDetail(null);
      resetCompose();
      emitMode('list');
    };
    window.addEventListener(closeDetailEventName, handleClose);
    return () => window.removeEventListener(closeDetailEventName, handleClose);
  }, [closeDetailEventName, emitMode, resetCompose]);

  useEffect(() => {
    if (!closeImageEventName) return undefined;
    const handleClose = () => {
      setImageModalOpen(false);
      if (detail?.note?.note_id) emitMode({ type: 'detail', noteId: detail.note.note_id });
    };
    window.addEventListener(closeImageEventName, handleClose);
    return () => window.removeEventListener(closeImageEventName, handleClose);
  }, [closeImageEventName, detail, emitMode]);

  const uploadViaServer = async (file) => {
    return uploadNoteImageThroughApi(file);
  };

  const newImagePreviewUrls = useMemo(
    () => newImages.map((file) => ({
      file,
      url: typeof URL.createObjectURL === 'function' ? URL.createObjectURL(file) : '',
    })),
    [newImages]
  );

  useEffect(() => {
    return () => {
      if (typeof URL.revokeObjectURL !== 'function') return;
      newImagePreviewUrls.forEach((preview) => {
        if (preview.url) URL.revokeObjectURL(preview.url);
      });
    };
  }, [newImagePreviewUrls]);

  const uploadNoteImage = async (file) => {
    const uploaded = await uploadViaServer(file);
    return {
      key: uploaded?.key,
      originalName: uploaded?.originalName || file.name,
      contentType: uploaded?.contentType || file.type,
    };
  };

  const handleFiles = (event) => {
    const pickedFiles = Array.from(event.target.files || []);
    const imageFiles = pickedFiles.filter((file) => /^image\//.test(file.type));
    if (pickedFiles.length !== imageFiles.length) {
      toastError('画像ファイルのみ追加できます。');
    }
    const files = imageFiles.filter((file) => file.size <= NOTE_IMAGE_MAX_BYTES);
    if (imageFiles.length !== files.length) {
      toastError(`1枚あたり${NOTE_IMAGE_MAX_MB}MBまでの画像を選択してください。`);
    }
    const remain = MAX_IMAGES - (existingImages.length - removedOrders.size) - newImages.length;
    const picked = files.slice(0, Math.max(0, remain));
    if (files.length > picked.length) {
      toastError(`画像は合計${MAX_IMAGES}枚までです。`);
    }
    setNewImages((prev) => [...prev, ...picked]);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleCreate = async (saveMode = 'direct') => {
    if (!canCreateNote) return;
    if (!ownerId) return;
    const trimmedTitle = titleValue.trim();
    const trimmedBody = bodyValue.trim();
    if (!trimmedTitle || !trimmedBody) {
      toastError('タイトルと本文を入力してください。');
      return;
    }
    try {
      setSaving(true);
      const uploaded = [];
      for (const file of newImages) {
        uploaded.push(await uploadNoteImage(file));
      }
      await createChatNoteByUrl(createUrl, {
        threadId: ownerId,
        eventId: ownerId,
        title: trimmedTitle,
        content: trimmedBody,
        images: uploaded,
        saveMode,
      });
      resetCompose();
      setDetail(null);
      emitMode('list');
      await fetchNotes(true);
      if (typeof onNotesChangedRef.current === 'function') {
        onNotesChangedRef.current({ type: saveMode === 'draft' ? 'draft-save' : 'create' });
      }
    } catch (error) {
      console.error('note create error:', error);
      toastError(getNoteErrorMessage(error, 'ノート作成に失敗しました。'));
    } finally {
      setSaving(false);
    }
  };

  const enterEdit = useCallback(async () => {
    if (!canManageNotes) return;
    if (!detail?.note) return;
    let editDetail = detail;
    const draftNoteId = String(detail.note.draft_note_id || '').trim();
    if (draftNoteId) {
      try {
        const baseUrl = buildDetailUrlRef.current(draftNoteId);
        const draftUrl = `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}version=draft`;
        editDetail = await getChatNoteDetailByUrl(draftUrl);
        setDetail(editDetail);
      } catch (error) {
        console.error('draft detail fetch error:', error);
        toastError(getNoteErrorMessage(error, '下書きの取得に失敗しました。'));
        return;
      }
    }
    setTitleValue(editDetail.note.title || '');
    setBodyValue(editDetail.note.content || '');
    setExistingImages(Array.isArray(editDetail.images) ? editDetail.images : []);
    setRemovedOrders(new Set());
    setNewImages([]);
    emitMode({ type: 'edit', note: editDetail.note });
  }, [canManageNotes, detail, emitMode, toastError]);

  const enterDraftEditById = useCallback(async (draftNoteId) => {
    if (!canManageNotes) return;
    const normalizedDraftNoteId = String(draftNoteId || '').trim();
    if (!normalizedDraftNoteId) return;
    try {
      const baseUrl = buildDetailUrlRef.current(normalizedDraftNoteId);
      const draftUrl = `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}version=draft`;
      const draftDetail = await getChatNoteDetailByUrl(draftUrl);
      setDetail(draftDetail);
      setTitleValue(draftDetail.note?.title || '');
      setBodyValue(draftDetail.note?.content || '');
      setExistingImages(Array.isArray(draftDetail.images) ? draftDetail.images : []);
      setRemovedOrders(new Set());
      setNewImages([]);
      emitMode({ type: 'edit', note: draftDetail.note });
    } catch (error) {
      console.error('draft detail fetch error:', error);
      toastError(getNoteErrorMessage(error, '下書きの取得に失敗しました。'));
    }
  }, [canManageNotes, emitMode, toastError]);

  const handleUpdate = async (saveMode = 'direct') => {
    if (!canManageNotes) return;
    const noteId = detail?.note?.note_id;
    if (!noteId) return;
    try {
      setSaving(true);
      const uploaded = [];
      for (const file of newImages) {
        uploaded.push(await uploadNoteImage(file));
      }
      await updateChatNoteByUrl(buildUpdateUrlRef.current(noteId), {
        title: titleValue.trim() || null,
        content: bodyValue.trim() || null,
        removeImages: Array.from(removedOrders.values()).sort((a, b) => a - b),
        addImages: uploaded,
        saveMode,
      });
      resetCompose();
      setDetail(null);
      emitMode('list');
      await fetchNotes(true);
      if (typeof onNotesChangedRef.current === 'function') {
        onNotesChangedRef.current({ type: saveMode === 'draft' ? 'draft-save' : 'update', noteId });
      }
    } catch (error) {
      console.error('note update error:', error);
      toastError(getNoteErrorMessage(error, 'ノート更新に失敗しました。'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (noteId) => {
    if (!canManageNotes) return;
    if (!noteId) return;
    if (!(await confirmDialog({ message: 'このノートを削除しますか？', danger: true }))) return;
    try {
      setSaving(true);
      await deleteChatNoteByUrl(buildDeleteUrlRef.current(noteId));
      resetCompose();
      setDetail(null);
      emitMode('list');
      await fetchNotes(true);
    } catch (error) {
      console.error('note delete error:', error);
      toastError(getNoteErrorMessage(error, 'ノート削除に失敗しました。'));
    } finally {
      setSaving(false);
    }
  };

  const openImageModal = (index) => {
    setImageModalIndex(index);
    setImageModalOpen(true);
    emitMode({ type: 'imgmodal', idx: index });
  };

  const downloadNoteImage = useCallback(async (image, index) => {
    const href = image?.url || image?.proxy_url || '';
    if (!href) return;
    try {
      const blob = await downloadBlobFromUrl(href, 'include');
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = getNoteImageDownloadName(image, index);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch {
      const link = document.createElement('a');
      link.href = href;
      link.download = getNoteImageDownloadName(image, index);
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      link.remove();
    }
  }, []);

  const handleDeleteImage = useCallback(async (image) => {
    if (!canManageNotes) return;
    const noteId = detail?.note?.note_id;
    const imageOrder = Number(image?.image_order);
    if (!noteId || !Number.isFinite(imageOrder)) return;
    if (!(await confirmDialog({ message: 'この画像を削除しますか？', danger: true }))) return;
    try {
      setSaving(true);
      await updateChatNoteByUrl(buildUpdateUrlRef.current(noteId), {
        title: null,
        content: null,
        removeImages: [imageOrder],
        addImages: [],
      });
      setImageModalOpen(false);
      await fetchNotes(true);
      await openDetail(noteId);
      if (typeof onNotesChangedRef.current === 'function') {
        onNotesChangedRef.current({ type: 'image-delete', noteId, imageOrder });
      }
    } catch (error) {
      console.error('note image delete error:', error);
      toastError(getNoteErrorMessage(error, '画像削除に失敗しました。'));
    } finally {
      setSaving(false);
    }
  }, [toastError, confirmDialog, canManageNotes, detail, fetchNotes, openDetail]);

  const imageItems = useMemo(() => Array.isArray(detail?.images) ? detail.images : [], [detail]);
  const selectedImage = imageItems[imageModalIndex] || null;
  const selectedImageSrc = pickNoteImageFullSrc(selectedImage);
  const selectedImageFallbackSrc = pickNoteImageFallbackSrc(selectedImage);

  const renderList = () => (
    <div className="notes-list-area">
      {!notes.length && loading ? (
        <ul className="notes-list" role="status" aria-live="polite">
          {Array.from({ length: 3 }, (_, index) => (
            <li key={`note-skeleton-${index}`} className="note-card notes-card" aria-hidden="true">
              <div className="note-card__main">
                <SkeletonLine width={index === 1 ? '58%' : '74%'} height={14} />
                <SkeletonLine width="92%" height={11} />
                <SkeletonLine width={index === 2 ? '64%' : '82%'} height={11} />
                {index === 0 ? (
                  <div className="note-card__thumb-wrap">
                    <SkeletonMedia className="note-card__thumb" />
                  </div>
                ) : null}
                <div className="note-card__meta">
                  <SkeletonAvatar className="note-avatar note-avatar--lg" size={32} />
                  <SkeletonLine width={96} height={11} />
                  <SkeletonLine width={72} height={10} />
                </div>
              </div>
              <div className="note-card__actions">
                <SkeletonButton width={32} height={30} />
              </div>
            </li>
          ))}
          <span className="sr-only">ノートを読み込み中...</span>
        </ul>
      ) : !notes.length ? (
        <div className="notes-empty">
          <div>{emptyText}</div>
          {canManageNotes && drafts.length ? (
            <div className="notes-empty-actions">
              <span className="note-card__draft-badge">下書き {drafts.length}件</span>
              <DsButton type="button" variant="secondary" size="sm" onClick={() => enterDraftEditById(drafts[0]?.note_id)}>
                下書きを編集
              </DsButton>
            </div>
          ) : null}
        </div>
      ) : (
        <ul className="notes-list">
          {notes.map((note) => {
            const thumbUrl = note.first_image_proxy_url || note.first_image_url || '';
            const moreCount = Math.max(0, Number(note.image_count || 0) - 1);
            const authorImage = buildProfileImageUrl(note);
            return (
              <li key={note.note_id} className="note-card notes-card">
                <button className="note-card__main" type="button" onClick={() => openDetail(note.note_id)} title="ノートを開く">
                  {note.title ? (
                    <div className="note-card__title">
                      {note.title}
                      {note.draft_note_id ? <span className="note-card__draft-badge">下書きあり</span> : null}
                    </div>
                  ) : null}
                  {note.content ? <div className="note-card__body">{note.content}</div> : null}
                  {thumbUrl ? (
                    <div className="note-card__thumb-wrap">
                      <NoteImage
                        image={{ proxy_url: thumbUrl, url: note.first_image_url }}
                        className="note-card__thumb"
                        alt=""
                        loading="lazy"
                      />
                      {moreCount > 0 ? <span className="note-card__thumb-count">+{moreCount}枚</span> : null}
                    </div>
                  ) : null}
                  <div className="note-card__meta">
                    <span className="note-avatar note-avatar--lg" style={authorImage ? { backgroundImage: `url(${authorImage})`, backgroundSize: 'cover', backgroundPosition: 'center' } : undefined}>
                      {!authorImage ? getAuthorInitial(note) : null}
                    </span>
                    <span className="note-author">{note.author_display_name || '作成者不明'}</span>
                    <span className="note-time">{formatDateTimeJST(note.created_at)}</span>
                  </div>
                </button>
                <div className="note-card__actions">
                  {canManageNotes && note?.permissions?.can_edit ? (
                    <button type="button" className="note-action" onClick={() => openDetail(note.note_id)} title="詳細を開く">
                      <i className="fa-solid fa-pen-to-square" aria-hidden="true" />
                    </button>
                  ) : null}
                  {canManageNotes && note?.permissions?.can_delete ? (
                    <button type="button" className="note-action delete" onClick={() => handleDelete(note.note_id)} title="削除">
                      <i className="fa-solid fa-trash" aria-hidden="true" />
                    </button>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {loading && notes.length ? (
        <div className="notes-loadmore" role="status" aria-live="polite">読み込み中…</div>
      ) : null}
      {hasMore && !loading ? (
        <div className="notes-loadmore">
          <DsButton type="button" variant="secondary" size="sm" onClick={() => fetchNotes(false)}>もっと見る</DsButton>
        </div>
      ) : null}
    </div>
  );

  const renderForm = (isEdit) => {
    const activeCount = existingImages.filter((image) => !removedOrders.has(Number(image.image_order))).length + newImages.length;
    return (
      <div className="notes-form-area">
        <div className="notes-form-row">
          <label className="notes-form-label">タイトル</label>
          <input className="notes-input" value={titleValue} onChange={(event) => setTitleValue(event.target.value)} maxLength={120} />
        </div>
        <div className="notes-form-row">
          <label className="notes-form-label">本文</label>
          <textarea className="notes-textarea" rows={8} value={bodyValue} onChange={(event) => setBodyValue(event.target.value)} />
        </div>
        {existingImages.length ? (
          <div className="notes-form-row">
            <label className="notes-form-label">既存画像</label>
            <div className="notes-existing-images">
              {existingImages.map((image) => {
                const imageOrder = Number(image.image_order || 0);
                const removed = removedOrders.has(imageOrder);
                const expiryLabel = formatNoteImageExpiryLabel(image.expires_at);
                return (
                  <div key={`${image.file_name}-${imageOrder}`} className={`notes-existing-thumb${removed ? ' is-removed' : ''}`}>
                    <NoteImage image={image} alt="" />
                    {expiryLabel ? <div className="notes-existing-expiry">{expiryLabel}</div> : null}
                    {!removed ? (
                      <button type="button" className="notes-existing-remove" onClick={() => setRemovedOrders((prev) => new Set(prev).add(imageOrder))}>×</button>
                    ) : (
                      <button type="button" className="notes-existing-undo" onClick={() => setRemovedOrders((prev) => {
                        const next = new Set(prev);
                        next.delete(imageOrder);
                        return next;
                      })}>戻す</button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}
        <div className="notes-form-row">
          <label className="notes-form-label">画像を追加（最大 {MAX_IMAGES} 枚）</label>
          <div className="notes-img-hint">現在 {activeCount} / {MAX_IMAGES} 枚</div>
          <input ref={fileRef} type="file" accept="image/*" multiple onChange={handleFiles} />
          {newImagePreviewUrls.length ? (
            <div className="notes-images-preview">
              {newImagePreviewUrls.map((preview, index) => (
                <span key={`${preview.file.name}-${index}`} className="notes-image-chip">
                  <img src={preview.url} alt="" className="notes-image-preview-thumb" />
                  <button
                    type="button"
                    className="notes-chip-remove"
                    aria-label={`${preview.file.name || '画像'}を削除`}
                    onClick={() => setNewImages((prev) => prev.filter((_, fileIndex) => fileIndex !== index))}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          ) : null}
        </div>
        <div className="notes-form-actions">
          <DsButton type="button" variant="secondary" className="notes-secondary-action" onClick={() => emitMode(detail?.note ? { type: 'detail', noteId: detail.note.note_id } : 'list')} disabled={saving}>キャンセル</DsButton>
          <DsButton type="button" variant="secondary" className="notes-secondary-action" onClick={() => (isEdit ? handleUpdate('draft') : handleCreate('draft'))} disabled={saving}>
            {saving ? '保存中…' : '下書き保存'}
          </DsButton>
          {isEdit && detail?.note?.status !== 'draft' ? (
            <DsButton type="button" variant="secondary" className="notes-secondary-action" onClick={() => handleUpdate('direct')} disabled={saving}>
              {saving ? '保存中…' : '直接更新'}
            </DsButton>
          ) : null}
          <DsButton type="button" variant="primary" block className="notes-submit-action" onClick={() => (isEdit ? handleUpdate('publish') : handleCreate('direct'))} disabled={saving}>
            {saving ? '保存中…' : isEdit ? '公開する' : '作成する'}
          </DsButton>
        </div>
      </div>
    );
  };

  const renderDetail = () => {
    if (!detail?.note) return null;
    const note = detail.note;
    const authorImage = buildProfileImageUrl(note);
    return (
      <div className="notes-detail-area">
        <div className="nd-header">
          <div className="nd-header__main">
            <div className="nd-title">{note.title}</div>
            <div className="nd-meta">
              <span className="note-avatar note-avatar--lg" style={authorImage ? { backgroundImage: `url(${authorImage})`, backgroundSize: 'cover', backgroundPosition: 'center' } : undefined}>
                {!authorImage ? getAuthorInitial(note) : null}
              </span>
              <span>{note.author_display_name || '作成者不明'}</span>
              <span>{formatDateTimeJST(note.updated_at || note.created_at)}</span>
            </div>
          </div>
          <div className="nd-actions">
            {canManageNotes && note?.draft_note_id ? (
              <button type="button" className="note-action" onClick={enterEdit} title="下書きを編集">
                <i className="fa-solid fa-file-pen" aria-hidden="true" />
              </button>
            ) : null}
            {canManageNotes && detail?.permissions?.can_edit ? (
              <button type="button" className="note-action" onClick={enterEdit} title="編集">
                <i className="fa-solid fa-pen-to-square" aria-hidden="true" />
              </button>
            ) : null}
          </div>
        </div>
        <div className="nd-content">{renderContentWithLinks(note.content)}</div>
        {imageItems.length ? (
          <div className="nd-gallery">
            {imageItems.map((image, index) => {
              const expiryLabel = formatNoteImageExpiryLabel(image.expires_at);
              return (
                <div key={`${image.file_name}-${index}`} className="nd-gallery__card">
                  <button type="button" className="nd-gallery__item" onClick={() => openImageModal(index)}>
                    <NoteImage image={image} alt="" />
                  </button>
                  <button type="button" className="nd-gallery__save" onClick={() => downloadNoteImage(image, index)}>
                    保存
                  </button>
                  {expiryLabel ? <div className="nd-gallery__expiry">{expiryLabel}</div> : null}
                  {canManageNotes && detail?.permissions?.can_edit ? (
                    <div className="nd-gallery__actions">
                      <button
                        type="button"
                        className="nd-gallery__action delete"
                        onClick={() => handleDeleteImage(image)}
                        disabled={saving}
                      >
                        {saving ? '削除中…' : '削除'}
                      </button>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <section className={rootClassName}>
      <div className="notes-header">
        <div className="notes-header__title">
          <i className="fa-solid fa-note-sticky" aria-hidden="true" />
          <span>{title}</span>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {mode !== 'list' ? (
            <button type="button" className="notes-back-action" onClick={() => emitMode('list')}>
              <i className="fa-solid fa-arrow-left" aria-hidden="true" />
              <span>戻る</span>
            </button>
          ) : null}
          {canCreateNote ? (
            <button type="button" className="notes-add-action" onClick={() => { resetCompose(); setDetail(null); emitMode('create'); }}>
              <span className="notes-add-action__icon"><i className="fa-solid fa-plus" aria-hidden="true" /></span>
              <span className="notes-add-action__label">ノートを作成</span>
            </button>
          ) : null}
        </div>
      </div>
      {mode === 'list' ? renderList() : null}
      {mode === 'create' ? renderForm(false) : null}
      {mode?.type === 'edit' ? renderForm(true) : null}
      {mode?.type === 'detail' ? renderDetail() : null}
      <ChatImageModal
        isOpen={imageModalOpen && imageItems.length > 0}
        src={selectedImageSrc}
        fallbackSrc={selectedImageFallbackSrc}
        onClose={() => {
          setImageModalOpen(false);
          if (detail?.note?.note_id) emitMode({ type: 'detail', noteId: detail.note.note_id });
        }}
        onPrev={imageItems.length > 1 ? () => setImageModalIndex((prev) => (prev - 1 + imageItems.length) % imageItems.length) : undefined}
        onNext={imageItems.length > 1 ? () => setImageModalIndex((prev) => (prev + 1) % imageItems.length) : undefined}
        showNav={imageItems.length > 1}
      />
    </section>
  );
};

export default ChatNotesPanel;
